import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { formatDateTime } from '../lib/format'

/**
 * Feature requests — what Renee asked Pearl for, and what Chris said back.
 *
 * This is the record; the email Pearl sends is only the nudge. A request with no
 * `notified_at` is one that never got emailed (Resend unconfigured, or a send
 * that failed) — it is flagged here rather than hidden, because the failure is
 * invisible from anywhere else and the whole point of saving it first was that
 * the request survives a broken mailbox.
 *
 * Renee can see this page too, deliberately. She should be able to tell that
 * what she asked for landed somewhere, and see when it gets picked up.
 */

type FeatureRequest = {
  id: number
  title: string
  request: string
  why: string | null
  area: string | null
  status: Status
  response: string | null
  requested_by_name: string | null
  created_at: string
  notified_at: string | null
}

const STATUSES = ['new', 'planned', 'building', 'done', 'declined'] as const
type Status = (typeof STATUSES)[number]

/** Reusing the outreach status pills rather than inventing a second palette. */
const PILL: Record<Status, string> = {
  new: 's-pending',
  planned: 's-queued',
  building: 's-drafted',
  done: 's-sent',
  declined: 's-skipped',
}

const isClosedStatus = (s: Status) => s === 'done' || s === 'declined'

export default function Requests() {
  const [rows, setRows] = useState<FeatureRequest[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [showClosed, setShowClosed] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('feature_requests')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) setError(error.message)
    else setRows((data ?? []) as FeatureRequest[])
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Save one row and leave every other row alone.
   *
   * Deliberately no reload afterwards. Re-fetching the list replaces every row
   * in state, which silently reverts any reply typed into another request that
   * has not been saved yet — and several open requests with half-written replies
   * is the normal state of this screen. What was just written is what is on
   * screen, so there is nothing to fetch.
   */
  async function save(r: FeatureRequest) {
    setSavingId(r.id)
    setError(null)
    const { error } = await supabase
      .from('feature_requests')
      .update({ status: r.status, response: r.response, updated_at: new Date().toISOString() })
      .eq('id', r.id)
    setSavingId(null)
    if (error) return setError(error.message)
    setToast(
      isClosedStatus(r.status) && !showClosed
        ? `Saved — "${r.title}" is closed and has moved out of the list.`
        : 'Saved',
    )
    // Long enough to read a sentence, not just the word "Saved". A request
    // vanishing off the screen with no explanation reads as a bug.
    window.setTimeout(() => setToast(null), isClosedStatus(r.status) ? 4000 : 1800)
  }

  const update = (id: number, patch: Partial<FeatureRequest>) =>
    setRows((prev) => (prev ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)))

  // Only a failure to LOAD leaves nothing to show. A failure to save is
  // rendered above the list instead, because replacing the page would throw away
  // every reply typed on it — punishing the user for the database's bad day.
  if (!rows) return <div className="empty">{error ?? 'Loading…'}</div>

  /**
   * Done and declined fold away, they are not deleted.
   *
   * A finished request still has to be readable — Renee should be able to see
   * that what she asked for landed and what came back — and this table is
   * deliberately append-only for exactly that reason: `20260814150000` grants
   * update on `status`, `response` and `notified_at` and nothing else, so the
   * record of what was asked cannot be edited or removed at all. The problem
   * being solved here is a list that grows forever, and hiding solves that
   * without touching the record.
   *
   * Closed rows are still fetched, so the count below is real rather than a
   * guess, and showing them is instant.
   */
  const isClosed = (r: FeatureRequest) => r.status === 'done' || r.status === 'declined'
  const openRows = rows.filter((r) => !isClosed(r))
  const closedRows = rows.filter(isClosed)
  const shown = showClosed ? rows : openRows

  return (
    <div className="board tpl">
      {error && <p className="error">Couldn't save: {error}</p>}

      <div className="notice">
        Things Renee has asked Pearl for. <strong>{openRows.length} still open.</strong> Nothing
        here gets built because it was asked for — this is a list of requests, not a plan.
        {closedRows.length > 0 && (
          <>
            {' '}
            <button className="btn ghost sm" onClick={() => setShowClosed((v) => !v)}>
              {showClosed
                ? `Hide the ${closedRows.length} closed`
                : `Show ${closedRows.length} closed`}
            </button>
          </>
        )}
      </div>

      {rows.length === 0 && (
        <div className="hint">
          Nothing yet. Renee can raise one by telling Pearl what she wishes the dashboard
          did.
        </div>
      )}

      {rows.length > 0 && shown.length === 0 && (
        <div className="hint">
          Nothing open. {closedRows.length} {closedRows.length === 1 ? 'request has' : 'requests have'}
          {' '}been closed — press <strong>Show {closedRows.length} closed</strong> to read them.
        </div>
      )}

      {shown.map((r) => (
        <section className="sec" key={r.id} style={{ marginBottom: 14 }}>
          <h3>
            {r.title}
            <span className={`pill ${PILL[r.status]}`}>{r.status}</span>
          </h3>

          <div className="emeta">
            <span>
              {r.requested_by_name ?? 'Unknown'} · {formatDateTime(r.created_at)}
              {r.area && <span className="hint"> · {r.area}</span>}
            </span>
            {!r.notified_at && (
              <span className="hint" title="Saved here, but the notification email did not go out">
                not emailed
              </span>
            )}
          </div>

          <div className="dbody">{r.request}</div>

          {r.why && (
            <>
              <div className="hint" style={{ marginTop: 10, marginBottom: 4 }}>
                What she's trying to achieve
              </div>
              <div className="dbody">{r.why}</div>
            </>
          )}

          <div className="hint" style={{ marginBottom: 4, marginTop: 12 }}>
            Status
          </div>
          <select
            className="areasel-d"
            value={r.status}
            onChange={(e) => update(r.id, { status: e.target.value as Status })}
          >
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          <div className="hint" style={{ marginBottom: 4, marginTop: 12 }}>
            Reply — Renee can read this
          </div>
          <textarea
            rows={3}
            value={r.response ?? ''}
            placeholder="Nothing yet."
            onChange={(e) => update(r.id, { response: e.target.value })}
          />

          <div className="savebar">
            <button className="btn" onClick={() => void save(r)} disabled={savingId === r.id}>
              {savingId === r.id ? 'Saving…' : 'Save'}
            </button>
            <span className="status">Status and reply are shared — Renee sees them too.</span>
          </div>
        </section>
      ))}

      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
